import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import MachineryPicker from './MachineryPicker';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * Create a new serialized component. Serial number + name + MFR part number
 * are required; the backend enforces uniqueness per (serial number, MFR part
 * number) pair — the same serial may exist across different part numbers
 * (OEMs stamp original parts with the parent equipment's serial). Status
 * starts at "new" by default; picking "available" is fine for parts that
 * already had a life somewhere the system didn't track.
 *
 * `applicable_machinery_ids` mirrors the bulk-inventory behavior: it's a
 * compatibility list, and the Install picker uses it to filter parts to
 * "fits this equipment". Empty list = applies to any equipment.
 */
const AddSerializedComponentDialog = ({ open, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    serial_number: '',
    name: '',
    mfr_part_number: '',
    manufacturer: '',
    model: '',
    status: 'new',
    notes: '',
  });
  const [applicableIds, setApplicableIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [bulkMatch, setBulkMatch] = useState(null);

  const reset = () => {
    setForm({
      serial_number: '',
      name: '',
      mfr_part_number: '',
      manufacturer: '',
      model: '',
      status: 'new',
      notes: '',
    });
    setApplicableIds([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.serial_number.trim() || !form.name.trim() || !form.mfr_part_number.trim()) {
      toast.error('Serial number, part name, and MFR part number are required');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.get(`${API}/serialized-components/bulk-match`, {
        params: { mfr_part_number: form.mfr_part_number.trim() },
      });
      if (data.match && data.match.quantity >= 1) {
        setBulkMatch(data.match);
        setSubmitting(false);
        return;
      }
    } catch (err) {
      toast.error('Could not check bulk inventory for this part number. Try again.');
      setSubmitting(false);
      return;
    }
    await save(false);
  };

  const save = async (convertFromBulk) => {
    setSubmitting(true);
    try {
      const payload = {
        serial_number: form.serial_number.trim(),
        name: form.name.trim(),
        mfr_part_number: form.mfr_part_number.trim(),
        manufacturer: form.manufacturer.trim() || null,
        model: form.model.trim() || null,
        applicable_machinery_ids: applicableIds,
        status: form.status,
        notes: form.notes.trim() || null,
        convert_from_bulk: convertFromBulk,
      };
      const res = await axios.post(`${API}/serialized-components`, payload);
      toast.success(convertFromBulk
        ? `Converted 1 bulk unit to SN ${res.data.serial_number}`
        : `Added SN: ${res.data.serial_number}`);
      window.dispatchEvent(new Event('inventory-changed'));
      setBulkMatch(null);
      reset();
      onSuccess && onSuccess(res.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add serialized component');
      setBulkMatch(null);
    } finally {
      setSubmitting(false);
    }
  };

  const convertPrompt = bulkMatch && (
    <AlertDialog open onOpenChange={(v) => { if (!v && !submitting) setBulkMatch(null); }}>
      <AlertDialogContent data-testid="sc-convert-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Convert a bulk unit?</AlertDialogTitle>
          <AlertDialogDescription data-testid="sc-convert-description">
            Bulk inventory has {bulkMatch.quantity} {bulkMatch.unit || 'ea'} of{' '}
            <span className="font-mono">{bulkMatch.part_number}</span> ({bulkMatch.name}).
            Convert 1 of them into serialized part SN {form.serial_number.trim()}? Bulk quantity
            becomes {bulkMatch.quantity - 1}, and the conversion is recorded in that part&apos;s History.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-wrap gap-2">
          <AlertDialogCancel disabled={submitting} data-testid="sc-convert-cancel">Cancel</AlertDialogCancel>
          <Button type="button" variant="outline" disabled={submitting} onClick={() => save(false)}
            data-testid="sc-convert-no">No, it&apos;s an additional part</Button>
          <Button type="button" disabled={submitting} onClick={() => save(true)}
            data-testid="sc-convert-yes">{submitting ? 'Saving…' : 'Convert 1 unit'}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-serialized-dialog">
        <DialogHeader>
          <DialogTitle>Add Serialized Component</DialogTitle>
          <DialogDescription>
            Register a unique wear part (valve, head cover, actuator) so its
            time in service can be tracked as it moves between installation points.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sc-serial">Serial Number *</Label>
              <Input
                id="sc-serial"
                data-testid="sc-serial-input"
                value={form.serial_number}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                placeholder="VAL-12345"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sc-name">Part Name *</Label>
              <Input
                id="sc-name"
                data-testid="sc-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="10 in Check Valve"
                required
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sc-mfr-pn">MFR Part Number *</Label>
            <Input
              id="sc-mfr-pn"
              data-testid="sc-mfr-pn-input"
              value={form.mfr_part_number}
              onChange={(e) => setForm({ ...form, mfr_part_number: e.target.value })}
              placeholder='e.g. "CV-10-316", "HC-8100-A"'
              required
            />
            <p className="text-xs text-slate-500">
              The manufacturer&apos;s part number stamped on the part. All units with
              the same MFR PN are grouped as one part family (case-insensitive) — a
              bulk inventory row is auto-created so you can set a shared reorder level.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sc-mfr">Manufacturer</Label>
              <Input
                id="sc-mfr"
                data-testid="sc-mfr-input"
                value={form.manufacturer}
                onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sc-model">Model</Label>
              <Input
                id="sc-model"
                data-testid="sc-model-input"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Applies to equipment (optional)</Label>
            <MachineryPicker
              value={applicableIds}
              onChange={setApplicableIds}
              disabled={submitting}
            />
            <p className="text-xs text-slate-500">
              Leave empty if the part fits anywhere. Selecting equipment
              restricts the Install picker on those installation points so
              wrong-fit parts stay out of the wrong holes.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sc-status">Starting Status</Label>
            <select
              id="sc-status"
              data-testid="sc-status-select"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="new">New (fresh from vendor)</option>
              <option value="available">Available (on the shelf, ready to install)</option>
            </select>
            <p className="text-xs text-slate-500">
              To place this part at an installation point, add it here first,
              then use the &quot;Install&quot; button on the equipment page.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sc-notes">Notes</Label>
            <Textarea
              id="sc-notes"
              data-testid="sc-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Optional"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="sc-submit-btn">
              {submitting ? 'Adding…' : 'Add Component'}
            </Button>
          </div>
        </form>
        {convertPrompt}
      </DialogContent>
    </Dialog>
  );
};

export default AddSerializedComponentDialog;
